<script>
import AkLabel from '@components/v2/general/AkLabel';
import {Icon} from '@iconify/vue';

export default {
  components: {Icon, AkLabel},
  inject: {
    pSubmitted: {default: null},
    pDisabled: {default: null},
    pIgnoredValidator: {default: null},
  },
  props: {
    inline: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
      default: undefined,
    },
    modelValue: {
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: undefined,
    },
    className: {
      type: String,
      required: false,
      default: '',
    },
    inputClass: {
      type: String,
      required: false,
      default: '',
    },
    validator: {
      type: Object,
      required: false,
      default: undefined,
    },
    ignoredValidator: {
      type: Array,
      required: false,
      default: undefined,
    },
    submitted: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    required: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    displayLabel: {
      type: Boolean,
      required: false,
      default: true,
    },
    information: {
      type: String,
      required: false,
      default: undefined,
    },
    hideRequired: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      changed: false,
      displayValue: this.currentValue, // version formatée avec espaces
    };
  },
  emits: ['update:modelValue', 'value-change'],

  computed: {
    currentValue: {
      get: function () {
        return this.modelValue;
      },
      set: function (val) {
        this.$emit('update:modelValue', val);
      },
    },
    isInline() {
      return this.inline;
    },
    getPlaceholder() {
      return this.placeholder ? this.placeholder : this.label;
    },
    alreadySubmitted() {
      if (this.submitted !== undefined) return this.submitted;
      if (this.pSubmitted !== undefined) return this.pSubmitted;
      return false;
    },
    isDisabled() {
      if (this.disabled !== undefined) return this.disabled;
      if (this.pDisabled !== undefined) return this.pDisabled;
      return false;
    },
    getIgnoredValidator() {
      if (this.ignoredValidator != undefined) return this.ignoredValidator;
      if (this.pIgnoredValidator != undefined) return this.pIgnoredValidator;
      return [];
    },
    hasValidator() {
      return this.validator !== undefined;
    },
    isInvalid() {
      if (this.hasValidator && this.getIgnoredValidator.length > 0) {
        if (this.validator.$errors) {
          for (let error of this.validator.$errors) {
            if (this.getIgnoredValidator.indexOf(error.$validator) === -1) {
              return true;
            }
          }
        } else {
          if (this.hasValidator) {
            for (let p of Object.keys(this.validator)) {
              if (
                this.getIgnoredValidator.indexOf(p) === -1 &&
                this.validator[p] === false &&
                p.startsWith('$') === false &&
                this.validator.$invalid
              ) {
                return true;
              }
            }
          } else return false;
        }
      } else {
        return this.hasValidator && this.validator.$invalid;
      }
      return false;
    },
    isRequired() {
      return (
        this.hasValidator &&
        this.validator.required !== undefined &&
        (this.validator.required.$params === undefined ||
          this.validator.required.$params.type === 'required' ||
          (this.validator.required.$params.type === 'requiredIf' && this.validator.required.$params.prop === true))
      );
    },
    isRequiredFailed() {
      return (
        this.isRequired &&
        this.getIgnoredValidator.indexOf('required') === -1 &&
        (this.validator.required.$invalid || (!this.validator.required && this.validator.$invalid)) &&
        this.alreadySubmitted
      );
    },

    isPhone() {
      return this.hasValidator && this.validator.phoneValidator !== undefined;
    },
    isPhoneFailed() {
      return (
        this.isPhone &&
        (this.validator.phoneValidator.$invalid ||
          (this.validator.phoneValidator === false && this.validator.$invalid === true)) &&
        this.alreadySubmitted
      );
    },
    getPhoneMessage() {
      if (this.isPhone && this.isPhoneFailed && this.validator.phoneValidator.$message) {
        return this.validator.phoneValidator.$message;
      }
      return this.$t('check_phone_number');
    },

    hasLabel() {
      return this.label !== undefined && this.displayLabel;
    },
  },
  methods: {
    removeSpaces(val) {
      return val.replace(/\s+/g, '');
    },
    onInput(val) {
      this.displayValue = val;
      const clean = this.removeSpaces(val);
      this.$emit('update:modelValue', clean);
    },
  },
  watch: {
    modelValue: {
      handler(newVal, oldVal) {
        if (!oldVal) {
          this.displayValue = newVal;
        }
        this.changed = true;
      },
      immediate: true,
    },
  },
};
</script>

<template>
  <div class="w-full input-layout form-control" :class="[className]">
    <AkLabel
      v-if="label && !inline && displayLabel"
      :required="(this.isRequired || this.required === true) && !hideRequired"
      :information="this.information">
      {{ label }}
    </AkLabel>
    <vue-tel-input
      v-model="displayValue"
      :disabled="this.isDisabled"
      @onInput="this.onInput"
      :class="[inputClass, {'p-invalid': isInvalid && alreadySubmitted}]"
      :inputOptions="{placeholder: getPlaceholder, type: 'tel'}"></vue-tel-input>
    <div v-if="!isInline" class="flex flex-col flex-1">
      <small v-if="isRequiredFailed" class="p-error">
        {{ $t('field_required', [this.label]) }}
      </small>
      <small v-if="isPhoneFailed" class="p-error">
        {{ this.getPhoneMessage }}
      </small>
    </div>
  </div>
</template>
